import { useTranslation } from "react-i18next"

import { AlertTriangle, ArrowRight } from "@/components/icons"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"

export type TenderConflict = {
  id: string
  claimAId: string
  claimALabel: string
  claimBId: string
  claimBLabel: string
  description: string
  severity: "low" | "medium" | "high"
}

type ConflictAlertProps = {
  conflicts: TenderConflict[]
}

const SEVERITY_CLASS: Record<TenderConflict["severity"], string> = {
  high: "border-destructive bg-destructive/[0.04]",
  medium: "border-warning bg-warning/[0.04]",
  low: "border-border bg-muted/50",
}

const SEVERITY_TITLE_CLASS: Record<TenderConflict["severity"], string> = {
  high: "text-destructive",
  medium: "text-warning-foreground",
  low: "text-muted-foreground",
}

const SEVERITY_ICON_CLASS: Record<TenderConflict["severity"], string> = {
  high: "size-4 text-destructive",
  medium: "size-4 text-warning",
  low: "size-4 text-muted-foreground",
}

export function ConflictAlert({ conflicts }: ConflictAlertProps) {
  const { t } = useTranslation()
  if (conflicts.length === 0) return null

  return (
    <div className="space-y-2">
      <h4 className="text-destructive flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="size-4" />
        {t("tender.conflictAlert.heading", { count: conflicts.length })}
      </h4>
      {conflicts.map((conflict) => (
        <Alert key={conflict.id} className={SEVERITY_CLASS[conflict.severity]}>
          <AlertTriangle className={SEVERITY_ICON_CLASS[conflict.severity]} />
          <AlertTitle className={SEVERITY_TITLE_CLASS[conflict.severity]}>
            {t("tender.conflictAlert.itemTitle", {
              claimA: conflict.claimAId,
              claimB: conflict.claimBId,
            })}
          </AlertTitle>
          <AlertDescription className="space-y-2">
            <p className="text-sm">{conflict.description}</p>
            <div className="flex flex-wrap gap-2">
              <Button className="gap-1.5" size="sm" variant="outline">
                <ArrowRight className="size-3" />
                {t("tender.conflictAlert.jumpTo", { id: conflict.claimAId })}
              </Button>
              <Button className="gap-1.5" size="sm" variant="outline">
                <ArrowRight className="size-3" />
                {t("tender.conflictAlert.jumpTo", { id: conflict.claimBId })}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ))}
    </div>
  )
}
